import { z } from "zod";

import { ExtractAtomicOperationFromDefinition } from "../../../atomic-operations/index.js";
import {
  CellReferencesParseError,
  CellReferencesV2,
  StoredCellReferencesV3,
  StoredJinjaSqlReferences,
  StoredSqlReferencesV3,
} from "../../../code/index.js";
import { DATA_FRAME_SQL_CONNECTION_ID } from "../../../constants.js";
import { CellOutputType } from "../../../enums.js";
import { DataConnectionId, SqlCellId } from "../../../idTypeBrands.js";
import { makeUpdateCellContentsOperation } from "../makeUpdateCellContentsOperation.js";

export interface SqlCellUpdatableFields {
  source: string;
  /**
   * perms on this are manually checked server side
   */
  connectionId: DataConnectionId | DATA_FRAME_SQL_CONNECTION_ID | null;
  resultVariable: string;
  useRichDisplay: boolean;
  enablePreview: boolean;
  loadIntoDataFrame: boolean;
  cellReferencesV2: CellReferencesV2 | null;
  cellReferencesParseError: CellReferencesParseError | null;
  sqlCellReferencesV3: StoredSqlReferencesV3 | null;
  jinjaCellReferencesV3: StoredCellReferencesV3 | null;
  jinjaSqlReferences: StoredJinjaSqlReferences | null;
  castDecimals: boolean;
  useNativeDates: boolean;
  sqlCellOutputType: CellOutputType;
}

const _UpdateSqlCellSafeFields: {
  [key in keyof SqlCellUpdatableFields]: "";
} = {
  source: "",
  connectionId: "",
  resultVariable: "",
  useRichDisplay: "",
  enablePreview: "",
  loadIntoDataFrame: "",
  cellReferencesV2: "",
  cellReferencesParseError: "",
  sqlCellReferencesV3: "",
  jinjaCellReferencesV3: "",
  jinjaSqlReferences: "",
  castDecimals: "",
  useNativeDates: "",
  sqlCellOutputType: "",
};
export const UpdateSqlCellSafeFields = Object.keys(_UpdateSqlCellSafeFields);

export const UPDATE_SQL_CELL = makeUpdateCellContentsOperation<
  SqlCellUpdatableFields,
  SqlCellId,
  {
    /**
     * Optional boolean to override existing logic for running the cell after
     * updating the sql cell. This is currently only implemented for field
     * `source` and will be a no-op for other fields.
     */
    runCell?: boolean;
  }
>()({
  type: "UPDATE_SQL_CELL",
  fields: {
    source: z.string(),
    connectionId: z
      .union([DataConnectionId, z.literal(DATA_FRAME_SQL_CONNECTION_ID)])
      .nullable(),
    resultVariable: z.string(),
    useRichDisplay: z.boolean(),
    enablePreview: z.boolean(),
    loadIntoDataFrame: z.boolean(),
    cellReferencesV2: CellReferencesV2.nullable(),
    cellReferencesParseError: CellReferencesParseError.nullable(),
    sqlCellReferencesV3: StoredSqlReferencesV3.nullable(),
    jinjaCellReferencesV3: StoredCellReferencesV3.nullable(),
    jinjaSqlReferences: StoredJinjaSqlReferences.nullable(),
    castDecimals: z.boolean(),
    useNativeDates: z.boolean(),
    sqlCellOutputType: z.nativeEnum(CellOutputType),
  },
  entityName: "SqlCell",
  fieldName: "sqlCellId",
  additionalFields: { runCell: z.boolean().default(false) },
});
export type UPDATE_SQL_CELL = ExtractAtomicOperationFromDefinition<
  typeof UPDATE_SQL_CELL
>;
